import { typesenseClient } from './client';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULTS } from '@/constants/app.constants';
import { 
  transformLORMetadata, 
  extractLORMetadata, 
  generateLORSearchText,
  validateLORConstraints,
  getLORMetadataSummary 
} from './lorTransformationUtils';

export interface IncrementalSyncOptions {
  batchSize: number;
  retryAttempts: number;
  retryDelay: number;
  enableLogging: boolean;
}

export interface SyncResult {
  success: boolean;
  sequenceId: string;
  operation: 'insert' | 'update' | 'delete';
  error?: string;
  retries: number;
}

export interface BatchSyncResult {
  total: number;
  successful: number;
  failed: number;
  results: SyncResult[];
  duration: number;
}

export class IncrementalSyncService {
  private options: IncrementalSyncOptions;

  constructor(options: Partial<IncrementalSyncOptions> = {}) {
    this.options = {
      batchSize: 50,
      retryAttempts: 3,
      retryDelay: 1000,
      enableLogging: true,
      ...options
    };
  }

  /**
   * Sync a single sequence to Typesense with enhanced genre data
   */
  async syncSequence(sequenceId: string, operation: 'insert' | 'update' | 'delete' = 'update'): Promise<SyncResult> {
    const startTime = Date.now();
    let retries = 0;

    while (retries <= this.options.retryAttempts) {
      try {
        if (operation === 'delete') {
          await typesenseClient.collections('sequences_v2').documents(sequenceId).delete();
          return {
            success: true,
            sequenceId,
            operation,
            retries
          };
        }

        // Fetch sequence with all related data including LOR metadata
        const { data: sequence, error } = await supabase
          .from('sequences')
          .select(`
            *,
            songs (
              id,
              title,
              artist,
              album,
              spotify_track_id,
              spotify_duration_ms,
              spotify_popularity
            ),
            artists (
              id,
              name
            ),
            genres (
              id,
              name,
              slug,
              category,
              featured,
              sort_order,
              sequence_count,
              total_downloads,
              average_rating
            ),
            sequence_lor_meta (
              id,
              controller_type,
              channel_count_ac,
              software_version,
              prop_types,
              license_level_required,
              sequence_type_tags,
              import_notes
            )
          `)
          .eq('id', sequenceId)
          .single();

        if (error || !sequence) {
          throw new Error(`Failed to fetch sequence: ${error?.message || 'Not found'}`);
        }

        // Transform sequence for Typesense with enhanced genre data
        const document = this.transformSequenceForTypesense(sequence);

        // Upsert to Typesense
        await typesenseClient.collections('sequences_v2').documents().upsert(document);

        if (this.options.enableLogging) {
          console.log(`✅ Synced sequence ${sequenceId} to Typesense (${Date.now() - startTime}ms)`);
        }

        return {
          success: true,
          sequenceId,
          operation,
          retries
        };

      } catch (error) {
        retries++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        if (retries <= this.options.retryAttempts) {
          if (this.options.enableLogging) {
            console.warn(`⚠️ Retry ${retries}/${this.options.retryAttempts} for sequence ${sequenceId}: ${errorMessage}`);
          }
          await this.delay(this.options.retryDelay * retries);
        } else {
          if (this.options.enableLogging) {
            console.error(`❌ Failed to sync sequence ${sequenceId} after ${retries} attempts: ${errorMessage}`);
          }
          return {
            success: false,
            sequenceId,
            operation,
            error: errorMessage,
            retries
          };
        }
      }
    }

    return {
      success: false,
      sequenceId,
      operation,
      error: 'Max retries exceeded',
      retries
    };
  }

  /**
   * Sync multiple sequences in batches
   */
  async syncSequences(sequenceIds: string[], operation: 'insert' | 'update' | 'delete' = 'update'): Promise<BatchSyncResult> {
    const startTime = Date.now();
    const results: SyncResult[] = [];
    let successful = 0;
    let failed = 0;

    // Process in batches
    for (let i = 0; i < sequenceIds.length; i += this.options.batchSize) {
      const batch = sequenceIds.slice(i, i + this.options.batchSize);
      
      if (this.options.enableLogging) {
        console.log(`🔄 Processing batch ${Math.floor(i / this.options.batchSize) + 1}/${Math.ceil(sequenceIds.length / this.options.batchSize)}`);
      }

      // Process batch concurrently
      const batchPromises = batch.map(id => this.syncSequence(id, operation));
      const batchResults = await Promise.all(batchPromises);

      // Aggregate results
      batchResults.forEach(result => {
        results.push(result);
        if (result.success) {
          successful++;
        } else {
          failed++;
        }
      });

      // Add delay between batches
      if (i + this.options.batchSize < sequenceIds.length) {
        await this.delay(500);
      }
    }

    const duration = Date.now() - startTime;

    if (this.options.enableLogging) {
      console.log(`📊 Batch sync completed: ${successful} successful, ${failed} failed (${duration}ms)`);
    }

    return {
      total: sequenceIds.length,
      successful,
      failed,
      results,
      duration
    };
  }

  /**
   * Sync sequences that have been modified since a given timestamp
   */
  async syncModifiedSequences(since: Date): Promise<BatchSyncResult> {
    const { data: sequences, error } = await supabase
      .from('sequences')
      .select('id')
      .gte('updated_at', since.toISOString())
      .order('updated_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch modified sequences: ${error.message}`);
    }

    if (!sequences || sequences.length === 0) {
      return {
        total: 0,
        successful: 0,
        failed: 0,
        results: [],
        duration: 0
      };
    }

    const sequenceIds = sequences.map(s => s.id);
    return this.syncSequences(sequenceIds, 'update');
  }

  /**
   * Sync sequences that have failed to sync (from sync logs)
   */
  async syncFailedSequences(hoursBack: number = 24): Promise<BatchSyncResult> {
    const { data: failedSyncs, error } = await supabase
      .from('typesense_sync_logs')
      .select('sequence_id')
      .eq('status', 'error')
      .gte('created_at', new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString())
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch failed syncs: ${error.message}`);
    }

    if (!failedSyncs || failedSyncs.length === 0) {
      return {
        total: 0,
        successful: 0,
        failed: 0,
        results: [],
        duration: 0
      };
    }

    // Get unique sequence IDs
    const sequenceIds = [...new Set(failedSyncs.map(s => s.sequence_id))];
    return this.syncSequences(sequenceIds, 'update');
  }

  /**
   * Enhanced artist name lookup with multiple fallback strategies
   * This matches the logic used in the sequence detail page to ensure consistency
   */
  private getArtistName(sequence: any): string {
    const song = sequence.songs;
    const artist = sequence.artists;
    
    // First try: Direct artist join from database
    if (artist?.name && artist.name.trim() !== '') {
      return artist.name;
    }
    
    // Second try: Song artist field
    if (song?.artist && song.artist.trim() !== '') {
      return song.artist;
    }
    
    // Third try: Check if artist name is embedded in title
    // Common patterns: "Artist - Song Title" or "Song Title by Artist"
    if (sequence.title) {
      const title = sequence.title.toString();
      
      // Pattern: "Song Title - Artist" (extract artist from second part)
      const dashMatch = title.match(/^(.+?)\s*-\s*(.+)$/);
      if (dashMatch && dashMatch[2].trim() !== '' && !dashMatch[2].toLowerCase().includes('sequence') && !dashMatch[2].toLowerCase().includes('from')) {
        return dashMatch[2].trim();
      }
      
      // Pattern: "Song Title by Artist"
      const byMatch = title.match(/^(.+?)\s+by\s+(.+)$/i);
      if (byMatch && byMatch[2].trim() !== '') {
        return byMatch[2].trim();
      }
      
      // Pattern: "Song Title (feat. Artist)" or "Song Title (featuring Artist)"
      const featMatch = title.match(/^(.+?)\s*\((?:feat\.?|featuring)\s+(.+?)\)$/i);
      if (featMatch && featMatch[2].trim() !== '') {
        return featMatch[2].trim();
      }
    }
    
    // Fourth try: Check if name field has artist info
    if (sequence.name && sequence.name !== sequence.title) {
      const name = sequence.name.toString();
      const dashMatch = name.match(/^(.+?)\s*-\s*(.+)$/);
      if (dashMatch && dashMatch[2].trim() !== '' && !dashMatch[2].toLowerCase().includes('sequence') && !dashMatch[2].toLowerCase().includes('from')) {
        return dashMatch[2].trim();
      }
    }
    
    // Fifth try: Check description for "by Artist" pattern
    if (sequence.description) {
      const description = sequence.description.toString();
      const byMatch = description.match(/\bby\s+([A-Za-z\s]+?)(?:\s*\.|\s*,|\s*$)/i);
      if (byMatch && byMatch[1].trim() !== '') {
        return byMatch[1].trim();
      }
    }
    
    // Final fallback: Unknown Artist
    return 'Unknown Artist';
  }

  /**
   * Enhanced transform sequence for Typesense with normalized genre data and LOR metadata
   */
  private transformSequenceForTypesense(sequence: any): any {
    const song = sequence.songs;
    const artist = sequence.artists;
    const genre = sequence.genres;
    const lorMeta = sequence.sequence_lor_meta;

    // Normalize timestamps to int64 (seconds since epoch) as required by Typesense
    const createdAt = sequence.created_at ? new Date(sequence.created_at) : new Date();
    const updatedAt = sequence.updated_at ? new Date(sequence.updated_at) : createdAt;
    const createdAtSeconds = Math.floor(createdAt.getTime() / 1000);
    const updatedAtSeconds = Math.floor(updatedAt.getTime() / 1000);
    
    // Calculate genre popularity indicators
    const isPopularGenre = (genre?.sequence_count || 0) > 50;
    const isFeaturedGenre = genre?.featured || false;
    const isHolidayGenre = genre?.category === 'Holiday & Seasonal';
    
    // Calculate price range
    const price = sequence.price || 0;
    const priceRange = price <= 5 ? 'low' : price <= 15 ? 'medium' : 'high';
    
    // Calculate duration in minutes
    const durationMs = song?.spotify_duration_ms || sequence.duration_seconds * 1000 || 0;
    const durationMinutes = Math.floor(durationMs / 60000);
    
    // Extract artist name using smart fallback logic
    const extractedArtist = this.getArtistName(sequence);
    
    // Extract and transform LOR metadata
    const extractedLORMeta = extractLORMetadata(sequence);
    const transformedLORMeta = transformLORMetadata(extractedLORMeta);
    
    // Generate LOR-specific search text
    const lorSearchText = generateLORSearchText(transformedLORMeta);
    
    // Build search text with enhanced genre data and LOR metadata
    const searchText = [
      sequence.title,
      sequence.description,
      song?.title,
      extractedArtist,
      genre?.name,
      genre?.category,
      sequence.holiday_type,
      sequence.software_type,
      lorSearchText
    ].filter(Boolean).join(' ').toLowerCase();

      const doc: any = {
      id: sequence.id.toString(),
      name: sequence.name || sequence.title || '',
      title: sequence.title || '',
      description: sequence.description || '',
      price: price,
      created_at: createdAtSeconds,
      updated_at: updatedAtSeconds,
      user_id: sequence.user_id,
      // Additional fields to satisfy schema
      software_type: sequence.software_type || '',
      holiday_type: sequence.holiday_type || '',
      file_type: sequence.file_type || '',
      album: sequence.album || '',
      album_cover_url: sequence.album_cover_url || '',
      image_url: sequence.image_url || '',
      download_count: (sequence.download_count || 0) + (sequence.download_baseline || 0),
      
      // Song fields
      song_title: song?.title || '',
      song_artist: extractedArtist,
      song_genre: genre?.name || '',
      song_duration: durationMs ? Math.floor(durationMs / 1000) : 0,
      song_spotify_id: song?.spotify_track_id || '',
      
      // Artist field for Typesense schema compatibility
      artist_name: extractedArtist,
      
      // Enhanced genre fields
      genre_id: genre?.id || '',
      genre_name: genre?.name || '',
      genre_slug: genre?.slug || genre?.name?.toLowerCase().replace(/\s+/g, '-') || '',
      genre_canonical: genre?.name || '',
      genre_category: genre?.category || 'Other',
      genre_featured: genre?.featured || false,
      genre_sequence_count: genre?.sequence_count || 0,
      genre_total_downloads: genre?.total_downloads || 0,
      genre_average_rating: genre?.average_rating || 0,
      genre_sort_order: genre?.sort_order || 999,
      genre_mapping_status: 'mapped',
      
      // Legacy fields for backward compatibility
      artist: extractedArtist,
      genre: genre?.name || '',
      
      // Search text
      search_text: searchText,
      
      // Filter fields
      price_range: priceRange,
      duration_minutes: durationMinutes,
      has_spotify: !!song?.spotify_track_id,
      is_free: price === 0,
      
      // Genre popularity indicators
      is_popular_genre: isPopularGenre,
      is_featured_genre: isFeaturedGenre,
       is_holiday_genre: isHolidayGenre,

       // Multi-genre fields
       primaryGenre: sequence.primary_genre || '',
       secondaryGenre: sequence.secondary_genre || '',
       genres: Array.isArray(sequence.genres) ? sequence.genres : [],
       
       // Light-O-Rama (LOR) metadata fields (transformed and validated)
       ...transformedLORMeta
    };

    // Fields expected by the live Typesense schema (defaults when absent)
    doc.claim_status = sequence.claim_status || '';
    doc.original_creator_info = sequence.original_creator_info || '';
    doc.claim_requested_at = sequence.claim_requested_at ? Math.floor(new Date(sequence.claim_requested_at).getTime() / 1000) : 0;
    doc.claim_verified_at = sequence.claim_verified_at ? Math.floor(new Date(sequence.claim_verified_at).getTime() / 1000) : 0;
    doc.file_url = sequence.file_url || '';
    return doc;
  }

  /**
   * Check if Typesense is healthy
   */
  async checkHealth(): Promise<boolean> {
    try {
      const health = await typesenseClient.health.retrieve();
      return health.ok || false;
    } catch (error) {
      console.error('Typesense health check failed:', error);
      return false;
    }
  }

  /**
   * Get sync statistics
   */
  async getSyncStats(): Promise<{
    totalSequences: number;
    typesenseCount: number;
    lastSyncTime?: string;
    failedSyncs24h: number;
  }> {
    try {
      // Get total sequences in Supabase
      const { count: totalSequences } = await supabase
        .from('sequences')
        .select('id', { count: 'exact', head: true });

      // Get count in Typesense
      const typesenseResult = await typesenseClient
        .collections('sequences_v2')
        .documents()
        .search({
          q: '*',
          per_page: 1
        });

      // Get last successful sync
      const { data: lastSync } = await supabase
        .from('typesense_sync_logs')
        .select('created_at')
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Get failed syncs in last 24 hours
      const { count: failedSyncs24h } = await supabase
        .from('typesense_sync_logs')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'error')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      return {
        totalSequences: totalSequences || 0,
        typesenseCount: typesenseResult.found || 0,
        lastSyncTime: lastSync?.created_at,
        failedSyncs24h: failedSyncs24h || 0
      };
    } catch (error) {
      console.error('Failed to get sync stats:', error);
      throw error;
    }
  }

  /**
   * Utility function to add delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Create singleton instance
export const incrementalSyncService = new IncrementalSyncService();
export default incrementalSyncService; 